/**
 * Metadata + editor definitions for each proposal block type.
 * Used both by the admin block editor and the preview renderer.
 */
export const BLOCK_META = {
  hero: {
    label: "Hero",
    description: "Cabeçalho grande com nome do destinatário e mensagem pessoal.",
    icon: "Sparkles",
    dataOnly: false,
    fields: [
      { key: "kicker", label: "Etiqueta pequena (kicker)", type: "text" },
      { key: "recipient", label: "Destinatário (aceita {{contact.name}})", type: "text" },
      { key: "title_line1", label: "Título — linha 1", type: "text" },
      { key: "title_line2_prefix", label: "Título — linha 2 · prefixo", type: "text" },
      { key: "title_italic", label: "Título — palavra em itálico laranja", type: "text" },
      { key: "title_line2_suffix", label: "Título — linha 2 · sufixo", type: "text" },
      { key: "subtitle", label: "Subtítulo/mensagem (aceita {{proposal.personal_message}})", type: "textarea" },
    ],
  },
  text: {
    label: "Texto",
    description: "Bloco de texto genérico com título + corpo. Pode ter cartão lateral.",
    icon: "AlignLeft",
    fields: [
      { key: "layout", label: "Layout", type: "select", options: [
        { value: "two_col", label: "2 colunas (título + corpo com cartão)" },
        { value: "full", label: "Largura completa" },
      ]},
      { key: "kicker", label: "Etiqueta pequena (kicker)", type: "text" },
      { key: "title", label: "Título", type: "text" },
      { key: "body", label: "Corpo", type: "textarea" },
      { key: "card_title", label: "Cartão lateral · título", type: "text", showIf: { layout: "two_col" } },
      { key: "card_body", label: "Cartão lateral · corpo", type: "textarea", showIf: { layout: "two_col" } },
    ],
  },
  features_grid: {
    label: "Grelha de vantagens/passos",
    description: "Grelha 2 colunas com ícones. Pode usar dados dinâmicos ou lista inline.",
    icon: "LayoutGrid",
    fields: [
      { key: "kicker", label: "Etiqueta pequena", type: "text" },
      { key: "title", label: "Título", type: "text" },
      { key: "source", label: "Fonte de dados (opcional — ex: proposal.caring_advantages)", type: "text" },
      { key: "numbered", label: "Numerar itens (01, 02...)", type: "boolean" },
      { key: "items", label: "Itens (usados se fonte estiver vazia)", type: "items_list", fields: [
        { key: "title", label: "Título", type: "text" },
        { key: "description", label: "Descrição", type: "textarea" },
      ]},
    ],
  },
  image: {
    label: "Imagem",
    description: "Imagem full-width com legenda opcional.",
    icon: "Image",
    fields: [
      { key: "image_url", label: "Imagem", type: "image_upload" },
      { key: "caption", label: "Legenda (opcional)", type: "text" },
      { key: "alt", label: "Texto alternativo", type: "text" },
      { key: "max_height", label: "Altura máxima (px, opcional)", type: "number" },
    ],
  },
  image_text: {
    label: "Imagem + Texto",
    description: "Imagem à esquerda ou direita, com texto ao lado.",
    icon: "Image",
    fields: [
      { key: "kicker", label: "Etiqueta pequena", type: "text" },
      { key: "title", label: "Título", type: "text" },
      { key: "body", label: "Corpo", type: "textarea" },
      { key: "image_url", label: "Imagem", type: "image_upload" },
      { key: "image_position", label: "Posição da imagem", type: "select", options: [
        { value: "left", label: "Esquerda" },
        { value: "right", label: "Direita" },
      ]},
    ],
  },
  beneficiaries_card: {
    label: "Clientes (auto)",
    description: "Renderiza automaticamente os clientes do lead com tratamento formal (Senhora Dona / Senhor).",
    icon: "Users",
    dataOnly: true,
    fields: [
      { key: "kicker", label: "Etiqueta pequena", type: "text" },
      { key: "title", label: "Título (vazio → auto: Cliente / Clientes)", type: "text" },
    ],
  },
  pricing_card: {
    label: "Serviço + preço (auto)",
    description: "Renderiza automaticamente a opção primária + outras opções.",
    icon: "Euro",
    dataOnly: true,
    fields: [
      { key: "kicker", label: "Etiqueta pequena", type: "text" },
      { key: "show_other_options", label: "Mostrar outras opções", type: "boolean" },
      { key: "show_additional_notes", label: "Mostrar notas adicionais", type: "boolean" },
    ],
  },
  gestor_card: {
    label: "Gestor dedicado (auto)",
    description: "Foto + bio + contactos do gestor atribuído. Só aparece após o depósito estar pago.",
    icon: "UserCheck",
    dataOnly: true,
    fields: [
      { key: "kicker", label: "Etiqueta pequena", type: "text" },
      { key: "title", label: "Título (opcional)", type: "text" },
    ],
  },
  consultor_card: {
    label: "Consultor de Cuidados (auto)",
    description: "Foto + bio + contactos do Consultor. Só aparece na Proposta Final entre a RT agendada e o depósito pago.",
    icon: "UserCheck",
    dataOnly: true,
    fields: [
      { key: "kicker", label: "Etiqueta pequena", type: "text" },
      { key: "title", label: "Título (opcional)", type: "text" },
    ],
  },
  deposit_card: {
    label: "Caução / condições de arranque (auto)",
    description: "Só aparece se o plano de caução estiver definido no lead.",
    icon: "Shield",
    dataOnly: true,
    fields: [
      { key: "kicker", label: "Etiqueta pequena", type: "text" },
      { key: "title", label: "Título", type: "text" },
      { key: "intro", label: "Parágrafo introdutório", type: "textarea" },
      { key: "footnote", label: "Nota final", type: "textarea" },
    ],
  },
  timeline_list: {
    label: "Cronograma (auto)",
    description: "Renderiza automaticamente as fases da proposta.",
    icon: "Calendar",
    dataOnly: true,
    fields: [
      { key: "kicker", label: "Etiqueta pequena", type: "text" },
      { key: "title", label: "Título", type: "text" },
    ],
  },
  testimonials_cta: {
    label: "CTA de testemunhos",
    description: "Link para a página externa de testemunhos (URL em Branding).",
    icon: "Star",
    fields: [
      { key: "kicker", label: "Etiqueta pequena", type: "text" },
      { key: "title", label: "Título", type: "text" },
      { key: "body", label: "Corpo", type: "textarea" },
      { key: "button_label", label: "Texto do botão", type: "text" },
    ],
  },
  faq_list: {
    label: "FAQ (auto)",
    description: "Renderiza automaticamente as perguntas frequentes da proposta.",
    icon: "HelpCircle",
    dataOnly: true,
    fields: [
      { key: "kicker", label: "Etiqueta pequena", type: "text" },
      { key: "title", label: "Título", type: "text" },
    ],
  },
  observations_form: {
    label: "Formulário observações",
    description: "Formulário para o cliente deixar observações antes de aceitar.",
    icon: "MessageSquare",
    fields: [
      { key: "kicker", label: "Etiqueta pequena", type: "text" },
      { key: "title", label: "Título", type: "text" },
      { key: "body", label: "Instruções", type: "textarea" },
      { key: "placeholder", label: "Placeholder do textarea", type: "text" },
      { key: "submit_label", label: "Texto do botão", type: "text" },
    ],
  },
  acceptance_form: {
    label: "Aceitação digital",
    description: "Bloco de aceitação com nome + botão. Deve estar sempre presente.",
    icon: "CheckCircle2",
    fields: [
      { key: "kicker", label: "Etiqueta pequena", type: "text" },
      { key: "title", label: "Título", type: "text" },
      { key: "body", label: "Condições comerciais", type: "textarea" },
      { key: "input_placeholder", label: "Placeholder do input", type: "text" },
      { key: "button_label", label: "Texto do botão", type: "text" },
      { key: "security_note", label: "Nota de segurança", type: "text" },
    ],
  },
  spacer: {
    label: "Espaço vertical",
    description: "Adiciona espaço em branco entre blocos.",
    icon: "Minus",
    fields: [
      { key: "height", label: "Altura (px)", type: "number" },
    ],
  },
};

export const BLOCK_ORDER = [
  "hero", "text", "features_grid", "image", "image_text",
  "beneficiaries_card", "pricing_card", "consultor_card", "gestor_card",
  "deposit_card", "timeline_list", "testimonials_cta",
  "faq_list", "observations_form", "acceptance_form", "spacer",
];

export function createEmptyBlock(type) {
  const defaults = {
    hero: {
      kicker: "Proposta preparada exclusivamente para",
      recipient: "{{contact.name_with_treatment}}",
      title_line1: "Um plano de cuidado",
      title_line2_prefix: "",
      title_italic: "à altura",
      title_line2_suffix: " {{beneficiary.full_honorific_with_article}}.",
      subtitle: "{{proposal.personal_message}}",
    },
    text: { layout: "two_col", kicker: "", title: "", body: "", card_title: "", card_body: "" },
    features_grid: { kicker: "", title: "", source: "", numbered: false, items: [] },
    image: { image_url: "", caption: "", alt: "", max_height: 500 },
    image_text: { kicker: "", title: "", body: "", image_url: "", image_position: "right" },
    beneficiaries_card: { kicker: "Para quem cuidamos", title: "" },
    pricing_card: { kicker: "Serviço proposto", show_other_options: true, show_additional_notes: true },
    consultor_card: { kicker: "O seu consultor", title: "Quem preparou esta proposta consigo." },
    gestor_card: { kicker: "O seu gestor dedicado", title: "" },
    deposit_card: {
      kicker: "Condições de arranque",
      title: "A caução e a data prevista para começarmos.",
      intro: "O arranque envolve a mobilização e formação da equipa dedicada.",
      footnote: "",
    },
    timeline_list: { kicker: "Cronograma", title: "Da aceitação ao início do serviço." },
    testimonials_cta: { kicker: "Confiança comprovada", title: "", body: "", button_label: "Ver testemunhos" },
    faq_list: { kicker: "Perguntas frequentes", title: "Respostas às dúvidas mais comuns." },
    observations_form: {
      kicker: "Observações do cliente",
      title: "Alguma nota antes de aceitar?",
      body: "",
      placeholder: "",
      submit_label: "Enviar observação",
    },
    acceptance_form: {
      kicker: "Aceitação digital",
      title: "Está tudo pronto para começarmos.",
      body: "{{proposal.commercial_terms}}",
      input_placeholder: "O seu nome completo",
      button_label: "Aceitar Proposta",
      security_note: "Aceitação segura. Validade da proposta: {{proposal.validity_days}} dias.",
    },
    spacer: { height: 40 },
  };
  return {
    id: (crypto?.randomUUID?.() || String(Date.now())) + "-" + Math.random().toString(36).slice(2, 8),
    type,
    visible: true,
    content: defaults[type] || {},
  };
}
